import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { BreakpointService } from '@zayan-erp/shared';
import { NgClass } from '@angular/common';
import { BadgeModule } from '@openng/optimus-ui/badge';
import { TagModule } from '@openng/optimus-ui/tag';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { FormsModule } from '@angular/forms';
import { DividerModule } from '@openng/optimus-ui/divider';
import { rxResourceFixed } from '@zayan-erp/shared';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmApsService } from '../../../services/dsm-aps.service';
import { DsmDealOption, DsmPrintAddendumOption } from '../../../models/dsm-deal-options-view.models';
import { DsmApsAddendumPreviewComponent } from '../../dsm-aps-options/dsm-aps-addendum-preview/dsm-aps-addendum-preview.component';
import { DsmApsAddendumOptionsComponent } from '../../dsm-aps-options/dsm-aps-addendum-options/dsm-aps-addendum-options.component';
import { DsmApsAddendumDetailsComponent } from '../../dsm-aps-options/dsm-aps-addendum-details/dsm-aps-addendum-details.component';
import { DsmApsAddendumOptionDetailsComponent } from '../../dsm-aps-options/dsm-aps-addendum-option-details/dsm-aps-addendum-option-details.component';
import { DsmAddAddendumModalComponent } from '../../dsm-aps-options/dsm-add-addendum-modal/dsm-add-addendum-modal.component';

type DsmDealOptionWithExpandableView = DsmDealOption & { isExpanded: boolean };

@Component({
  selector: 'dsm-specs-lot-addendum-view',
  standalone: true,
  host: { class: 'flex flex-col flex-1 overflow-hidden' },
  imports: [
    BadgeModule,
    NgClass,
    TagModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    FormsModule,
    TooltipModule,
    DividerModule,
    DsmApsAddendumPreviewComponent,
    DsmApsAddendumOptionsComponent,
    DsmApsAddendumDetailsComponent,
    DsmApsAddendumOptionDetailsComponent,
    DsmAddAddendumModalComponent
  ],
  templateUrl: './dsm-specs-lot-addendum-view.component.html',
  styleUrl: './dsm-specs-lot-addendum-view.component.scss'
})
export class DsmSpecsLotAddendumViewComponent {
  buildSpecId = input.required<string>();

  readonly #dealOptionsService = inject(DsmDealOptionsService);
  readonly #dealContext = inject(DsmApsDealContextService);
  readonly #dsmApsService = inject(DsmApsService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #breakpointService = inject(BreakpointService);

  isMobile = toSignal(this.#breakpointService.isMobile$, { initialValue: false });

  readonly selectAllPrintCheckbox = viewChild<ElementRef<HTMLInputElement>>('selectAllPrint');

  addAddendumDialogVisible = signal(false);
  printAddendumDialogVisible = signal(false);
  printOptionSelections = signal<Record<string, boolean>>({});

  dealResource = rxResourceFixed({
    params: () => ({ lotId: this.buildSpecId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.lotId) return of(null);
      return this.#dsmApsService.getSpecLotDetails(params.lotId);
    }
  });

  // Build spec lots have no deal/contract yet — skip the deal/contracts calls
  // that 404'd before. Print button stays disabled because effectiveTargetDate is null.
  contractsResource = { value: () => null, reload: () => undefined };

  effectiveDeal = computed(() => this.dealResource.value() ?? null);
  effectiveDealId = computed(() => '');
  effectiveLotId = computed(() => this.effectiveDeal()?.id ?? '');
  effectiveModelId = computed(() => this.effectiveDeal()?.selectedModel?.id ?? '');
  effectiveElevationId = computed(() => this.effectiveDeal()?.selectedElevation?.id ?? '');
  isUnderContract = computed(() => false);
  effectiveTargetDate = computed(() => null);

  selectedAddendumId = signal<string | null>(null);
  selectedProductId = signal<string | null>(null);
  selectedAddendumIdsForPrint = signal<string[]>([]);
  selectedKitProductId: string | null = null;

  dealOptionsHistory: DsmDealOptionWithExpandableView[] = [];

  constructor() {
    effect(() => {
      if (this.#dealContext.reloadDeal()) {
        this.dealResource.reload();
        this.#dealContext.reloadDeal.set(false);
      }
    });
  }

  dealsOptionsRxResource = rxResourceFixed({
    params: () => ({ lotId: this.effectiveLotId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.lotId) return of([]);
      return this.#dealOptionsService.getLotAddendums(params.lotId, false);
    }
  });

  printAddendumOptionsResource = rxResourceFixed({
    params: () => ({ visible: this.printAddendumDialogVisible() }),
    defaultValue: [] as DsmPrintAddendumOption[],
    stream: ({ params }) => {
      if (!params.visible) return of([]);
      return this.#dealOptionsService.getPrintAddendumOptions();
    }
  });

  dealsOptions = linkedSignal<DsmDealOptionWithExpandableView[]>(() => {
    const options = this.dealsOptionsRxResource.value() ?? [];
    return options.map(option => {
      const isPreviouslyExpanded = this.dealOptionsHistory.find(history => history.id === option.id)?.isExpanded;
      const hasNoProducts = option.products.length === 0;
      if (isPreviouslyExpanded === false) {
        return { ...option, isExpanded: false } as DsmDealOptionWithExpandableView;
      }
      return {
        ...option,
        isExpanded: isPreviouslyExpanded || hasNoProducts
      } as DsmDealOptionWithExpandableView;
    });
  });

  onDealsOptionsSet = effect(() => {
    this.dealOptionsHistory = this.dealsOptions();
  });

  onPrintLookupOptionsSet = effect(() => {
    this.printAddendumOptionsResource.value();
    this.onPrintOptionsLoaded();
  });

  private readonly syncPrintSelectionWithOptions = effect(() => {
    const options = this.dealsOptions();
    const validIds = new Set(options.map(o => o.id));
    this.selectedAddendumIdsForPrint.update(sel => {
      const next = sel.filter(id => validIds.has(id));
      return next.length === sel.length && next.every((id, i) => id === sel[i]) ? sel : next;
    });
  });

  isAllAddendumsSelectedForPrint = computed(() => {
    const opts = this.dealsOptions();
    const n = opts.length;
    if (n === 0) return false;
    const sel = this.selectedAddendumIdsForPrint();
    return sel.length === n && opts.every(o => sel.includes(o.id));
  });

  printAddendumsButtonLabel = computed(() => {
    const count = this.selectedAddendumIdsForPrint().length;
    if (count === 0) return 'Print all addendums';
    return `Print ${count} addendums`;
  });

  private readonly syncSelectAllIndeterminate = effect(() => {
    const el = this.selectAllPrintCheckbox()?.nativeElement as any;
    if (!el) return;
    const total = this.dealsOptions().length;
    const selected = this.selectedAddendumIdsForPrint().length;
    el.indeterminate = total > 0 && selected > 0 && selected < total;
  });

  selectedAddendum = computed(
    () => this.dealsOptions().find(addendum => addendum.id === this.selectedAddendumId()) ?? null
  );

  showAddendumDetails = computed(() => this.selectedAddendum() !== null && this.selectedProductId() === null);
  showOptionDetails = computed(() => this.selectedAddendum() !== null && this.selectedProductId() !== null);

  onOptionChanges(): void {
    this.dealsOptionsRxResource.reload();
  }

  openAddAddendumDialog(): void {
    this.addAddendumDialogVisible.set(true);
  }

  onAddendumSaved(): void {
    this.addAddendumDialogVisible.set(false);
    this.dealsOptionsRxResource.reload();
  }

  closeMobileDetails(): void {
    this.selectedAddendumId.set(null);
    this.selectedProductId.set(null);
  }

  onAddendumSelect(optionId: string): void {
    this.selectedAddendumId.set(optionId);
    this.selectedProductId.set(null);
  }

  onAddendumProductSelect(addendumId: string, productId: string, isKitLinkedProduct = false): void {
    this.selectedAddendumId.set(addendumId);
    this.selectedProductId.set(productId);
    this.selectedKitProductId = isKitLinkedProduct ? productId : null;
  }

  isAddendumSelectedForPrint(id: string): boolean {
    return this.selectedAddendumIdsForPrint().includes(id);
  }

  onAddendumPrintToggle(addendumId: string, checked: boolean): void {
    this.selectedAddendumIdsForPrint.update(ids => {
      if (checked) {
        return ids.includes(addendumId) ? ids : [...ids, addendumId];
      }
      return ids.filter(i => i !== addendumId);
    });
  }

  onSelectAllPrintChange(checked: boolean): void {
    const dealIds = this.dealsOptions().map(o => o.id);
    this.selectedAddendumIdsForPrint.set(checked ? [...dealIds] : []);
  }

  openPrintAddendumDialog(): void {
    if (this.dealsOptions().length === 0) return;
    this.printAddendumDialogVisible.set(true);
    this.printAddendumOptionsResource.reload();
  }

  onPrintDialogVisibilityChange(visible: boolean): void {
    this.printAddendumDialogVisible.set(visible);
    if (visible) return;
    this.printOptionSelections.set({});
  }

  onPrintOptionsLoaded(): void {
    if (!this.printAddendumDialogVisible()) return;
    const options = this.printAddendumOptionsResource.value() ?? [];
    const defaults = options.reduce<Record<string, boolean>>((acc, option) => {
      acc[option.name] = !!option.isDefault;
      return acc;
    }, {});
    this.printOptionSelections.set(defaults);
  }

  isPrintOptionSelected(optionName: string): boolean {
    return !!this.printOptionSelections()[optionName];
  }

  onPrintOptionToggle(optionName: string, selected: boolean): void {
    this.printOptionSelections.update(current => ({ ...current, [optionName]: selected }));
  }

  submitPrintAddendums(): void {
    const dealId = this.effectiveDealId();
    if (!dealId || this.dealsOptions().length === 0) return;
    const selectedAddendumIds = this.selectedAddendumIdsForPrint();
    const addendumIds =
      selectedAddendumIds.length > 0 ? selectedAddendumIds : this.dealsOptions().map(addendum => addendum.id);
    const selectedOptionIds = this.buildSelectedOptionIds();
    if (!selectedOptionIds.length) return;

    const lotNo = this.effectiveDeal()?.lotNo;
    this.#dealOptionsService
      .downloadAddendumsPdf(dealId, addendumIds, selectedOptionIds, `${lotNo}-Addendum-${+new Date()}.pdf`)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.onPrintDialogVisibilityChange(false));
  }

  private buildSelectedOptionIds(): number[] {
    const options = this.printAddendumOptionsResource.value() ?? [];
    const selections = this.printOptionSelections();
    return options.filter(option => !!selections[option.name]).map(option => option.id);
  }
}
